// src/hooks/useTVData.js

import { useState, useEffect } from 'react';

const API_KEY = 'a1e72fd93ed59f56e6332813b9f8dcae'; // Keep your API key secure

/**
 * Custom hook to fetch and manage TV show seasons and episodes.
 * @param {object} item - The movie or TV show item.
 * @returns {object} - State and handlers for TV data.
 */
export default function useTVData(item) {
  const [seasons, setSeasons] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(item?.currentSeason || null);
  const [selectedEpisode, setSelectedEpisode] = useState(item?.currentEpisode || 1);
  const [isLoading, setIsLoading] = useState(false);

  const isEpisodeReleased = (airDate) => !airDate ? false : new Date(airDate) <= new Date();

  const getLatestReleasedEpisode = (episodesList) => {
    const released = episodesList?.filter(ep => isEpisodeReleased(ep.air_date)) || [];
    return released.length > 0 ? released[released.length - 1].episode_number : 1;
  };

  // Effect to fetch all seasons for a given TV show
  useEffect(() => {
    if (item?.media_type !== 'tv') {
      setSeasons([]);
      setEpisodes([]);
      return;
    }

    const fetchSeasons = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`https://api.themoviedb.org/3/tv/${item.id}?api_key=${API_KEY}`);
        const data = await res.json();
        const availableSeasons = data.seasons?.filter(s => s.season_number > 0).sort((a, b) => b.season_number - a.season_number) || [];
        setSeasons(availableSeasons);

        // Set the initial season if not already set (e.g., from recently watched)
        if (!item.currentSeason && availableSeasons.length > 0) {
          setSelectedSeason(availableSeasons[0].season_number);
        } else {
          setSelectedSeason(item.currentSeason || (availableSeasons.length > 0 ? availableSeasons[0].season_number : null));
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
      }
      // We don't set isLoading to false here, as the episode fetch will follow immediately
    };

    fetchSeasons();
  }, [item?.id, item?.media_type, item.currentSeason]); // Rerun only when the show changes

  // Effect to fetch episodes when the selected season changes
  useEffect(() => {
    if (item?.media_type !== 'tv' || !selectedSeason) {
        if (!isLoading) setIsLoading(false); // Ensure loading is off if no fetch happens
        return;
    }

    const fetchEpisodes = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`https://api.themoviedb.org/3/tv/${item.id}/season/${selectedSeason}?api_key=${API_KEY}`);
        const data = await res.json();
        const fetchedEpisodes = data.episodes || [];
        setEpisodes(fetchedEpisodes);

        // Determine the initial episode to select
        const currentEpExists = fetchedEpisodes.some(ep => ep.episode_number === item.currentEpisode);
        const currentEpReleased = currentEpExists ? isEpisodeReleased(fetchedEpisodes.find(ep => ep.episode_number === item.currentEpisode).air_date) : false;
        
        if (item.currentEpisode && selectedSeason === item.currentSeason && currentEpExists && currentEpReleased) {
             setSelectedEpisode(item.currentEpisode);
        } else {
             setSelectedEpisode(getLatestReleasedEpisode(fetchedEpisodes));
        }

      } catch (error) {
        console.error('Error fetching episodes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEpisodes();
  }, [selectedSeason, item?.id]); // Rerun only when the season changes

  return {
    seasons,
    episodes,
    selectedSeason,
    setSelectedSeason,
    selectedEpisode,
    setSelectedEpisode,
    isLoading,
    isEpisodeReleased,
  };
}